import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import React, { useState } from "react";
import type { TFunction } from "i18next";
import { Link } from "react-router-dom";
import { Controller } from "react-hook-form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "../../../../components";
import { CustomCheckbox } from "../../../../components/custom-checkbox";
import FormErrorText from "../../../../components/form/FormErrorText";
import type { LoginFormMethods, LoginFormValues } from "../types/login.types";

interface LoginFormProps {
  t: TFunction;
  loading: boolean;
  error: string | null;
  form: LoginFormMethods;
  onSubmit: (values: LoginFormValues) => Promise<void>;
  onMerchantRegister: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  t,
  loading,
  error,
  form,
  onSubmit,
  onMerchantRegister,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  return (
    <Card>
      <CardHeader className="space-y-1 pb-4">
        <CardTitle className="text-2xl font-bold text-center dark:text-white">
          {t("auth.welcome_back")}
        </CardTitle>
        <CardDescription className="text-center dark:text-gray-400">
          {t("auth.admin_login_subtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t("auth.email")}
            </Label>
            <div>
              <Input
                id="email"
                type="email"
                startIcon={<Mail className="h-4 w-4" />}
                placeholder={t("auth.enter_email")}
                disabled={loading}
                aria-invalid={errors.email ? "true" : "false"}
                aria-describedby={errors.email ? "email-error" : undefined}
                className={`h-11 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-emerald-500 dark:focus:ring-emerald-500 ${
                  errors.email ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""
                }`}
                {...register("email")}
              />
            </div>
            <FormErrorText
              id="email-error"
              message={errors.email?.message}
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t("auth.password")}
            </Label>
            <div>
              <Controller
                control={control}
                name="password"
                render={({ field }) => (
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    startIcon={<Lock className="h-4 w-4" />}
                    endIcon={
                      showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />
                    }
                    onEndIconClick={() => setShowPassword((current) => !current)}
                    endIconAriaLabel={
                      showPassword ? t("auth.hide_password") : t("auth.show_password")
                    }
                    placeholder={t("auth.enter_password")}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={loading}
                    aria-invalid={errors.password ? "true" : "false"}
                    aria-describedby={errors.password ? "password-error" : undefined}
                    className={`h-11 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-emerald-500 dark:focus:ring-emerald-500 ${
                      errors.password
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />
                )}
              />
            </div>
            <FormErrorText
              id="password-error"
              message={errors.password?.message}
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
            <label className="flex items-center cursor-pointer">
              <Controller
                control={control}
                name="rememberMe"
                render={({ field }) => (
                  <CustomCheckbox
                    id="rememberMe"
                    checked={field.value}
                    onChange={field.onChange}
                    disabled={loading}
                    size="sm"
                  />
                )}
              />
              <span className="ml-2 text-sm sm:text-base font-semibold text-[#75757d] dark:text-[#a0a0a0]">
                {t("auth.remember_me")}
              </span>
            </label>
            <Link
              to="/forgot-password"
              className="text-sm sm:text-base font-bold text-[#004231] dark:text-[#00c49a] hover:underline"
            >
              {t("auth.forgot_password")}
            </Link>
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <FormErrorText
                message={error}
                className="text-center"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="
              w-full h-11 md:h-[46px]
              bg-[#00C49A] hover:bg-[#00b38a] dark:hover:bg-[#00d4a8]
              text-[#004231] dark:text-[#004231]
              font-bold text-sm md:text-base
              rounded-[30px]
              focus:outline-none focus:ring-2 focus:ring-[#00c49a] focus:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200
              transform hover:scale-[1.02] active:scale-[0.98]
            "
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#004231] border-t-transparent mr-2"></div>
                {t("auth.signing_in")}
              </div>
            ) : (
              t("auth.log_in")
            )}
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#dcdcdc] dark:border-[#404040]"></div>
            </div>
          </div>

          <button
            type="button"
            onClick={onMerchantRegister}
            disabled={loading}
            className="
              w-full h-11 md:h-[46px]
              bg-white hover:bg-gray-50 dark:bg-[#2a2a2a] dark:hover:bg-[#333333]
              text-[#004231] dark:text-[#00c49a]
              border-2 border-[#004231] dark:border-[#00c49a]
              font-bold text-sm md:text-base
              rounded-[30px]
              focus:outline-none focus:ring-2 focus:ring-[#00c49a] focus:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200
              transform hover:scale-[1.02] active:scale-[0.98]
            "
          >
            {t("auth.merchant_registration")}
          </button>
        </form>
      </CardContent>
    </Card>
  );
};

export default LoginForm;
